import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  DASHBOARD_NAV_LABELS,
  DashboardMobileBar,
  DashboardSidebarDesktop,
  DashboardSidebarDrawer,
  DashboardSignInPrompt,
  useDashboardDrawer,
} from '../../components/dashboard/DashboardSidebar';
import { useAuth } from '../../context/AuthContext';
import './dashboard-theme.css';

/**
 * Candidate dashboard chrome. Sits below the website Navbar (so the marketing nav,
 * AvatarMenu, and Footer stay visible — analysis §5.2). Renders the sidebar to the
 * left and the active section's outlet on the right; on mobile the sidebar collapses
 * into an off-canvas drawer.
 *
 * Auth gating: candidate-only. Anonymous visitors see a sign-in prompt rather than a
 * redirect, so a deep link to e.g. `/dashboard/applications` lands them somewhere
 * informative instead of a generic signin form. Loading state is silent (parent
 * Navbar already covers that) — we just render an empty container until bootstrap
 * resolves.
 */
export function DashboardLayout() {
  const { candidate, isLoading } = useAuth();
  const { pathname } = useLocation();
  const { open, setOpen } = useDashboardDrawer();

  // Shared dialogs and drawers render into document.body. This route-scoped class
  // lets the candidate theme reach those portals without changing shared UI or logic.
  useEffect(() => {
    document.body.classList.add('qh-dashboard-active');
    return () => document.body.classList.remove('qh-dashboard-active');
  }, []);

  if (isLoading) {
    return <div className="qh-dashboard min-h-screen pt-8 sm:pt-10 pb-20 bg-gray-50" />;
  }

  if (!candidate) {
    const target = encodeURIComponent(pathname || '/dashboard/profile');
    return <Navigate to={`/signin?return=${target}`} replace />;
  }

  const activeLabel = DASHBOARD_NAV_LABELS[pathname] ?? 'Dashboard';

  return (
    <div className="qh-dashboard min-h-screen pt-8 sm:pt-10 pb-20 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="md:flex md:items-start md:gap-6">
          <DashboardSidebarDesktop />
          <DashboardSidebarDrawer open={open} onClose={() => setOpen(false)} />
          <main className="flex-1 min-w-0">
            <DashboardMobileBar onOpen={() => setOpen(true)} activeLabel={activeLabel} />
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

export { DashboardSignInPrompt };
